import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, AppBar, Toolbar, Typography, Button, Box, Container } from '@mui/material';
import { CalendarMonth, Code, Info } from '@mui/icons-material';
import { theme } from './theme';
import { HolidayProvider } from './contexts/HolidayContext';
import { HomePage } from './pages/HomePage';
import { ApiPage } from './pages/ApiPage';
import { CreditsPage } from './pages/CreditsPage';

const Navigation: React.FC = () => {
  const location = useLocation();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1 }}>
          <CalendarMonth sx={{ fontSize: { xs: 28, sm: 32 }, color: 'primary.main', mr: { xs: 1, sm: 1.5 } }} />
          <Typography
            variant="h5"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Australian Public Holidays
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              AU Holidays
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
            <Button
              component={Link}
              to="/"
              variant={location.pathname === '/' ? 'contained' : 'text'}
              startIcon={<CalendarMonth />}
              sx={{ 
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: '64px' },
                px: { xs: 1, sm: 2 },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: '0 8px 0 -4px' },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Calendar
              </Box>
            </Button>
            <Button
              component={Link}
              to="/api"
              variant={location.pathname === '/api' ? 'contained' : 'text'}
              startIcon={<Code />}
              sx={{ 
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: '64px' },
                px: { xs: 1, sm: 2 },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: '0 8px 0 -4px' },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                API
              </Box>
            </Button>
            <Button
              component={Link}
              to="/credits"
              variant={location.pathname === '/credits' ? 'contained' : 'text'}
              startIcon={<Info />}
              sx={{ 
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: '64px' },
                px: { xs: 1, sm: 2 },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: '0 8px 0 -4px' },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Credits
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <HolidayProvider>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navigation />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/api" element={<ApiPage />} />
              <Route path="/credits" element={<CreditsPage />} />
            </Routes>
          </Box>
        </HolidayProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
